import { getEnvConfig } from '@hbs-us/contracts';

import { getAppEnv } from '../../lib/env.js';

/**
 * Build the credentialed/public URLs for the estimate download + customer-approval
 * paths (D-06). NONE of these hosts are hardcoded (threat T-21-07): the api host
 * comes from `@hbs-us/contracts getEnvConfig().domains.api`; the legacy SSR public
 * estimate host is the current tenant origin (the SAME host the SPA shell is
 * served from — `getEnvConfig` exposes only the api host, not a per-tenant SSR
 * host, so it is derived from `window.location.origin`).
 *
 * - **Proposal PDF**: `estimatePdfUrl(id)` → `${apiBase}/api/v1/estimates/:id/pdf`
 *   — the existing api-host GET (proposal engine reused from 21-05, NOT rebuilt
 *   client-side). Opened via `<a>` / new tab with the user's cookies (safe GET).
 * - **Customer-approval link-out**: `estimateApprovalUrl(public_token)` → the
 *   LEGACY SSR public estimate page (`/estimate/view/:token`). The unauthenticated
 *   signature+approve canvas STAYS SSR (D-02 / A2 / Pitfall 2) — the SPA never
 *   re-mounts that flow; the detail view only surfaces this link to copy/open.
 *   The public estimate page is served from the tenant's own host
 *   (`{tenant}.<env>.qelm.app`), the SAME origin the SPA shell is served from.
 */

function apiBaseUrl(): string {
  return `https://${getEnvConfig(getAppEnv()).domains.api}`;
}

/** The legacy SSR public host = the current tenant origin (same host as the SPA shell). */
function publicBaseUrl(): string {
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

/** Credentialed proposal-PDF download for an estimate (api-host GET, engine reused). */
export function estimatePdfUrl(id: number): string {
  return `${apiBaseUrl()}/api/v1/estimates/${id}/pdf`;
}

/**
 * Public customer-approval page for an estimate (legacy SSR host, by opaque
 * token). The SPA links OUT to this — it never re-implements the signature
 * canvas (D-02 / A2). Returns `null` until a public token has been minted.
 */
export function estimateApprovalUrl(publicToken: string | null): string | null {
  if (!publicToken) return null;
  return `${publicBaseUrl()}/estimate/view/${publicToken}`;
}
